const UNSUPPORTED_DOCUMENT_PREFIX = 'UNSUPPORTED_DOCUMENT';

const UNSUPPORTED_POLICY_CODES = [
  'lifeInsurance',
  'rateAction',
  'notInsuranceDocument',
  'invalid',
] as const;

export type UnsupportedPolicyCode = (typeof UNSUPPORTED_POLICY_CODES)[number];

const UNSUPPORTED_DOCUMENT_MESSAGES: Record<UnsupportedPolicyCode, string> = {
  lifeInsurance:
    "We're expanding the types of policies we support. Right now we can't analyze life insurance documents without LTC benefits. Please upload a policy or illustration we support, or contact us if you have questions.",
  rateAction:
    "We're expanding the types of policies we support. This looks like a rate action or benefit change notice. If you'd like to upload a rate action document, please reach out to our team.",
  notInsuranceDocument:
    "This doesn't appear to be an illustration or application form. Please upload a policy document, or contact us if you have questions.",
  invalid:
    "We couldn't confidently identify this policy type from the document. Please try a clearer scan or PDF, or contact us if you have questions.",
};

export function isUnsupportedPolicyCode(code: string | undefined): code is UnsupportedPolicyCode {
  return UNSUPPORTED_POLICY_CODES.includes(code as UnsupportedPolicyCode);
}

export function buildUnsupportedDocumentError(code: UnsupportedPolicyCode): string {
  return `${UNSUPPORTED_DOCUMENT_PREFIX}:${code}`;
}

export function parseUnsupportedDocumentError(
  error: string,
): { code: UnsupportedPolicyCode; message: string } | null {
  if (!error.startsWith(`${UNSUPPORTED_DOCUMENT_PREFIX}:`)) return null;
  const code = error.slice(UNSUPPORTED_DOCUMENT_PREFIX.length + 1) as UnsupportedPolicyCode;
  if (!(code in UNSUPPORTED_DOCUMENT_MESSAGES)) return null;
  return { code, message: UNSUPPORTED_DOCUMENT_MESSAGES[code] };
}

/**
 * Builds the policy classification prompt for use with text-based (Chat Completions)
 * or vision-based (Responses API with PDF) classification.
 */

const POLICY_TYPE_DEFINITIONS = `
## **Policy Type Definitions and Codes**
Use the following descriptions to classify the policy:

- **Traditional Long-Term Care Insurance (longTermCareInsurance)**
  - Standalone LTC insurance policy, NOT attached to life or annuity products.
  - The primary feature is long-term care benefits, with no life insurance or investment component.
  - Payment Structure: Typically requires recurring premium payments (e.g., monthly, quarterly, or annually) for the policyholder's lifetime. No lump-sum premium option.Premiums are often subject to increases over time.
  - Benefit Structure: Provides a maximum lifetime benefit amount or a set dollar pool for LTC expenses. May include an elimination period before benefits begin. Benefits are typically reimbursement-based but may include indemnity payouts.

- **Life Insurance with LTC Rider (lifeInsuranceWithRider)**
  - A life insurance policy that includes an LTC **rider** (add-on).
  - The primary feature is life insurance, with a death benefit provided to beneficiaries.
  - Payment Structure: Premiums follow a life insurance model—can be fixed or variable. LTC riders may require additional costs or be included in the base policy.
  - Benefit Structure: LTC benefits are paid by accelerating the death benefit rather than providing additional coverage. No separate LTC benefit pool beyond the death benefit. If LTC benefits are never used, the full death benefit is available to beneficiaries.

- **Hybrid Life Insurance (hybridLifeInsurance)**
  - A life insurance policy that provides **dedicated** LTC benefits beyond just accelerating the death benefit.
  - Considered asset-based, typically designed for both th transfer and LTC coverage.
  - Payment Structure: Often requires a large single premium upfront (e.g., $50,000+), but may offer 5-pay, 10-pay, or limited pay structures. Some policies allow for annual payments, but they do not have lifetime recurring premiums. Premiums are fixed and do not increase.
  - Benefit Structure: Includes a separate LTC benefit pool, distinct from the death benefit. LTC benefits often exceed the death benefit (e.g., 2-3x the face amount). May include an extension of benefits or continuation of benefits. If LTC is never used, a death benefit is still paid to beneficiaries.


- **Short-Term Care Insurance (shortTermCareInsurance)**
  - Provides LTC benefits but for a **short duration (e.g., 12-24 months).**
  - Similar to traditional LTC insurance but with lower benefit amounts and a shorter payout period.
  - Payment Structure: Typically requires recurring monthly or annual premium payments. Underwriting may be simpler than traditional LTC insurance.
  - No lifetime benefit pool—payments stop after the fixed duration.

- **Annuity Hybrid (annuityHybrid)**
  - An annuity-based hybrid product with **long-term care benefits** attached.
  - Payment Structure: Purchased with a lump-sum annuity premium or structured payouts. Growth rates may be fixed, indexed, or variable, depending on market conditions.
  - Benefit Structure: LTC benefits are drawn directly from the annuity value. Some policies offer a multiplier effect, allowing LTC withdrawals at a rate greater than the annuity's cash value. If LTC is never used, the remaining annuity value is available for withdrawals or to pass to beneficiaries.
  - Keyword to look for is "Annuity"


- **Hybrid Life IRA (hybridLifeIra)**
  - A hybrid LTC policy funded using an IRA, 401(k), or annuity rollover.
  - Functions similarly to an annuity hybrid but is funded using tax-advantaged retirement accounts.
  - Benefit Structure: Provides LTC benefits funded from retirement accounts. May include a rollover option from existing qualified accounts. Benefits follow an annuity hybrid structure but with tax considerations.
  - Keywords to look for are "IRA", "retirement account"

  - **Life Insurance Without LTC Benefits (lifeInsurance)**
  - A life insurance policy (term life, whole life, universal life, variable life, variable universal life, indexed universal life, guaranteed universal life, or FIUL) that does **NOT** include any long-term care rider, chronic illness rider that pays for LTC, or LTC benefits.
  - If the document is clearly a life insurance policy or illustration with no mention of long-term care coverage, classify as "lifeInsurance".

  - **Rate Action or Benefit Change Notice (rateAction)**
  - A notification letter about premium rate increases, benefit reductions, reduced benefit options, or policy amendments.
  - These are administrative notices, not policy illustrations or contracts.
  - Keywords: "rate action", "rate increase", "premium increase notice", "reduced benefit option", "benefit change"

  - **Non-Insurance Document (notInsuranceDocument)**
  - Brochures, marketing materials, carrier sales materials, delivery checklists, application forms without policy details, or any document that is not an insurance policy or illustration.

  - **Unknown / Uncertain (invalid)**
  - Use this ONLY if you cannot confidently classify the document into any of the above categories.
  - **When in doubt, prefer classifying as a supported policy type.** If the document looks like it could be a policy or illustration with any mention of long-term care, chronic illness, or LTC-related benefits, classify it as the closest supported type rather than "invalid".
  - Examples of documents that are NOT LTC-related (use "invalid" or the closest unsupported category):
    - Health insurance policies **without** extended care benefits
    - Investment or annuity documents with **no reference** to LTC benefits
    - General life insurance policies **without** any LTC rider or chronic illness rider

---

## **Primary Policyholder Identification**
- You will be provided with a **full name**.
- Search the policy text for named individuals.
- Accept **common variations** or small differences in first names, including:
  - **Nicknames & Diminutives** (e.g., Bob → Robert, Rob → Robert, Liz → Elizabeth)
  - **Spelling Variations** (e.g., Sara → Sarah, Jon → John)
  - **Full vs. Shortened Names** (e.g., Will → William, Mike → Michael)
- If a clear match is found, return "isPrimaryPolicyHolder": true.
- If there is no match, return "isPrimaryPolicyHolder": false.
- If the policy lists **two names (joint policy)**, check if the provided name matches the **first person listed**.
- If there is no clear match, return "isPrimaryPolicyHolder": false.

---

## **Instructions**
- Carefully analyze the policy document and classify it into **one of the six supported policy types**. Only use an unsupported category when the document is **clearly** not an LTC-related policy.
- **When in doubt, always classify as a supported policy type.** If the document mentions long-term care, chronic illness riders, LTC benefits, or similar terms in any capacity, classify it as the closest supported type.
- Only return "policyCode": "lifeInsurance" if the document is **clearly** a life insurance policy with **no** LTC rider, chronic illness rider, or LTC benefits whatsoever.
- Only return "policyCode": "rateAction" if the document is **clearly** an administrative notice about rate increases or benefit changes, not a policy or illustration.
- Only return "policyCode": "notInsuranceDocument" if the document is **clearly** not an insurance policy (e.g. a brochure, checklist, or marketing material).
- Return "policyCode": "invalid" only as a last resort if you truly cannot classify the document. Prefer a supported type over "invalid" whenever possible.
- Identify whether LTC benefits come from a **rider, a separate benefit pool, an annuity, or a short-term structure**.
- Determine whether the policy covers **one individual or two individuals jointly**.
- Your response **must be structured as a JSON object** in the following format:

json
{
  "policyType": "[Select one of: Traditional Long-Term Care Insurance, Life Insurance with LTC Rider, Hybrid Life Insurance, Short-Term Care Insurance, Annuity Hybrid, Hybrid Life IRA, Life Insurance Without LTC Benefits, Rate Action or Benefit Change Notice, Non-Insurance Document, Invalid Document]",
  "policyCode": "[Select one of: longTermCareInsurance, lifeInsuranceWithRider, hybridLifeInsurance, shortTermCareInsurance, annuityHybrid, hybridLifeIra, lifeInsurance, rateAction, notInsuranceDocument, invalid]",
  "rationale": "[Provide a concise, clear explanation citing key policy terms. If unsupported or invalid, explain why.]",
  "isJoint": [true | false],
  "isPrimaryPolicyHolder": [true | false]
}
`;

/**
 * Builds the classification prompt when the policy text is available (e.g. extracted from PDF).
 * Used by the server for text-based inferGenericPolicyType (Chat Completions API).
 */
export function buildPolicyClassificationPromptForText(
  clientFullName: string,
  policyDocumentText: string,
): string {
  return `
  You are analyzing a long-term care (LTC) insurance policy document to classify its type. Carefully read the provided policy text and determine:

1. **policyType**: Identify the correct policy type based on the descriptions below.
2. **policyCode**: Return the corresponding code for the identified policy type.
3. **rationale**: Provide a clear explanation for your classification, citing relevant policy terms and phrases.
4. **isJoint**: Determine whether the policy covers one person or two individuals jointly. In certain cases, there may be multiple policyholders/applicants listed, but the option for joint/shared coverage is declined. There is typically a fee associated to having joint coverage and if there is explicit mention that the fee is $0, No, N/A, or similar, then return false.
5. **isPrimaryPolicyHolder**: Determine whether the provided person is the **first** person listed in the policy (i.e., the primary policyholder/applicant).

---
${POLICY_TYPE_DEFINITIONS}
---

## Input Parameters

### **Full name to Match**: ${clientFullName}

### **Policy Document**
${policyDocumentText}

--

### Your Response
Return only a JSON object in the exact format specified. Do not include any extra text.
  `;
}

/**
 * Builds the classification prompt when the policy is provided as an attached PDF (Responses API).
 * Used by the openai worker for Path B (scanned PDFs). The model will read the attached document.
 */
export function buildPolicyClassificationPromptForPdf(clientFullName: string): string {
  return `
  You are analyzing a long-term care (LTC) insurance policy document to classify its type. The policy document is attached as a PDF. Read the attached document and determine:

1. **policyType**: Identify the correct policy type based on the descriptions below.
2. **policyCode**: Return the corresponding code for the identified policy type.
3. **rationale**: Provide a clear explanation for your classification, citing relevant policy terms and phrases.
4. **isJoint**: Determine whether the policy covers one person or two individuals jointly. In certain cases, there may be multiple policyholders/applicants listed, but the option for joint/shared coverage is declined. There is typically a fee associated to having joint coverage and if there is explicit mention that the fee is $0, No, N/A, or similar, then return false.
5. **isPrimaryPolicyHolder**: Determine whether the provided person is the **first** person listed in the policy (i.e., the primary policyholder/applicant).

---
${POLICY_TYPE_DEFINITIONS}
---

## Input Parameters

### **Full name to Match**: ${clientFullName}

### **Policy Document**
The policy document is attached as a PDF. Read it and classify.

### Your Response
Return only a JSON object in the exact format specified. Do not include any extra text.
  `;
}
